import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CardContent, Stack, Box, Typography, TextField, Alert, Tabs, Tab } from '@mui/material';
import { LocalHospitalOutlined, BusinessOutlined } from '@mui/icons-material';
import { AuthenticatedTemplate } from "@azure/msal-react";
import { EncounterType } from '../../../services/EncounterService/types/encounterTypes';
import { useGetRegistrationQuery } from '../../../services/RegistrationService';
import { useAppDispatch, useAppSelector } from '../../../app/store';
import { setEncounterAction as setEncounter, clearEncounterAction as clearEncounter } from '../../../services/EncounterService/slice';
import { getEmptyEncounter } from '../../../services/EncounterService/emptyEncounter';
import { PageContainer, PageCard, SectionHeader, SectionPaper, PageLoading as Loading } from '../../common/components';
import GoBackButton from '../../common/components/styled-ui-elements/GoBackButton';
import PatientInfoView from '../components/views/PatientInfoView';
import { AdmitDateEdit, DischargeDateEdit, AdmissionTypeEdit, AdmissionSourceEdit, PhysicianSelectEdit } from '../components/fieldEditors';
import { CreateEncounterButton } from '../components/commands';
import { ServicesGrid } from '../components/grids';
import { ServiceAddDialog } from '../components/dialogs';

// Helper function to create initial encounter
const createInitialEncounter = (
  emptyEncounter: EncounterType, 
  company: string, 
  center: string, 
  mrn: string
): EncounterType => ({
  ...emptyEncounter,
  patient: {
    ...emptyEncounter.patient,
    mrn: mrn || emptyEncounter.patient?.mrn,
  },
  caseIdentification: {
    ...emptyEncounter.caseIdentification,
    company: company || emptyEncounter.caseIdentification?.company,
    centerId: center || emptyEncounter.caseIdentification?.centerId,
  }
});

const EncounterCreateContent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const mrn = searchParams.get('mrn') || '';
  const dispatch = useAppDispatch();

  // Starter state for EncounterType
  const emptyEncounter: EncounterType = getEmptyEncounter(company, center);

  // Create concatenated ID for API call when MRN is provided
  const registrationId = mrn ? `${company}-${center}-${mrn}` : '';

  // Use RTK Query to fetch registration data when MRN is provided
  const { data: registrationData, error: registrationError, isLoading: registrationLoading } = useGetRegistrationQuery(
    { id: registrationId }, 
    {
      skip: !mrn || !company || !center,
    }
  );

  useEffect(() => {
    let initialEncounter = createInitialEncounter(emptyEncounter, company, center, mrn);

    // If we have registration data, prepopulate patient information
    if (registrationData) {
      initialEncounter = {
        ...initialEncounter,
        patient: {
          ...initialEncounter.patient,
          mrn: registrationData.identifiers?.mrn || mrn,
          firstName: registrationData.name?.first || '',
          middleName: registrationData.name?.middle || '',
          lastName: registrationData.name?.last || '',
          birthDate: registrationData.demographics?.dateOfBirth || '',
          sex: registrationData.demographics?.gender || '',
          homePhone: registrationData.contactInfo?.phone || '',
          email: registrationData.contactInfo?.email || '',
        }
      };
    }
    
    dispatch(setEncounter(initialEncounter));
    setEncounterState(initialEncounter);
    
    return () => { dispatch(clearEncounter()); };
  }, [dispatch, company, center, mrn, registrationData]);

  const [encounter, setEncounterState] = useState<EncounterType>(emptyEncounter);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCreateSuccess = () => {
    setSuccess(true);
    setErrorMsg('');
  };

  const handleCreateError = (message: string) => {
    setErrorMsg(message);
    setSuccess(false);
  };

  // TabPanel component for tab content
  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

  function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            {children}
          </Box>
        )}
      </div>
    );
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          {/* Professional Header */}
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderBottom: '2px solid #3b82f6',
              p: 2.5,
              position: 'relative',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h5" component="h1" fontWeight={600} color="#1e293b" sx={{ mb: 0.5 }}>
                  Create New Encounter
                </Typography>
                <Typography variant="body2" color="#64748b" sx={{ fontSize: '0.9rem' }}>
                  {mrn ? `Creating encounter for MRN: ${mrn}` : 'Complete all required fields to create a new encounter'}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <CreateEncounterButton 
                  onSuccess={handleCreateSuccess}
                  onError={handleCreateError}
                />
                <GoBackButton />
              </Box>
            </Box>
          </Box>

          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <Stack spacing={2.5}>
              {/* Patient Information Section */}
              <PatientInfoView />

              {/* Encounter Information Section */}
              <Box>
                <SectionHeader 
                  icon={<LocalHospitalOutlined sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />} 
                  title="Encounter Information" 
                />
                <SectionPaper>
                  <Stack direction="row" gap={2} sx={{ mb: 2 }}>
                    <AdmitDateEdit />
                    <DischargeDateEdit />
                    <AdmissionSourceEdit />
                    <AdmissionTypeEdit />
                  </Stack>
                  
                  <Stack direction="row" gap={2} sx={{ mb: 2 }}>
                    <PhysicianSelectEdit />
                    <Box sx={{ flex: 1 }}></Box>
                  </Stack>
                </SectionPaper>
              </Box>

              {/* Tab Strip Section */}
              <Box>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={tabValue} onChange={handleTabChange} aria-label="encounter tabs">
                    <Tab label="Procedures" />
                    <Tab label="Insurance" />
                    <Tab label="Guarantor" />
                    <Tab label="Notes" />
                  </Tabs>
                </Box>
                
                <CustomTabPanel value={tabValue} index={0}>
                  <Stack spacing={0.5}>
                    <Box display="flex" justifyContent="flex-end" sx={{ mb: 0.5 }}>
                      <ServiceAddDialog />
                    </Box>
                    <ServicesGrid />
                  </Stack>
                </CustomTabPanel>
                
                <CustomTabPanel value={tabValue} index={1}>
                  <SectionPaper>
                    <Typography variant="body2" color="#64748b">
                      Insurance section - Coming soon
                    </Typography>
                  </SectionPaper>
                </CustomTabPanel>
                
                <CustomTabPanel value={tabValue} index={2}>
                  <SectionPaper>
                    <Typography variant="body2" color="#64748b">
                      Guarantor section - Coming soon
                    </Typography>
                  </SectionPaper>
                </CustomTabPanel>
                
                <CustomTabPanel value={tabValue} index={3}>
                  <SectionPaper>
                    <TextField
                      label="Notes"
                      multiline
                      rows={4}
                      fullWidth
                      size="small"
                      placeholder="Enter encounter notes..."
                    />
                  </SectionPaper>
                </CustomTabPanel>
              </Box>

              {/* Status Messages */}
              {registrationError && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Could not load patient registration data. You can still create the encounter by filling in the patient information manually.
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Encounter created successfully! {mrn && 'Redirecting to patient registration...'}
                </Alert>
              )}
              {(errorMsg) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errorMsg}
                </Alert>
              )}
            </Stack>
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

const EncounterCreate: React.FC = () => {
  return (
    <AuthenticatedTemplate>
      <EncounterCreateContent />
    </AuthenticatedTemplate>
  );
};

export default EncounterCreate;
